import React from 'react';
import { useCreateCategory } from 'pages/app/Events/gql/mutations/useCreateCategory';
import { useGetAllCategories } from 'pages/app/Events/gql/queries/useGetAllCategories';
import SearchableSelect from '../SearchableSelect';

/**
 * CategorySelect - A searchable/creatable dropdown for ticket categories
 *
 * @param {string|number} value - The selected category ID
 * @param {Function} onChange - Callback when selection changes (receives {label, value} or null)
 * @param {Function} onSuccess - Callback when a new category is created (receives category name)
 * @param {Function} onError - Callback when category creation fails (receives error)
 * @param {string} label - Field label (default: "Category")
 * @param {string} placeholder - Placeholder text
 * @param {...props} props - Additional props passed to SearchableSelect
 */
const CategorySelect = ({
  value,
  onChange,
  onSuccess,
  onError,
  ticketType,
  label = 'Category',
  placeholder = 'Search or create category...',
  ...props
}) => {
  const { data: categoriesData } = useGetAllCategories();
  const { createCategory, loading: creatingCategory } = useCreateCategory();

  const categoryOptions = (categoriesData?.getAllCategories || []).map(
    (cat) => ({
      value: cat.id.toString(),
      label: cat.name,
    })
  );

  const handleCreateCategory = async (name) => {
    try {
      const newCategory = await createCategory(name, ticketType);
      if (newCategory) {
        if (onSuccess) {
          onSuccess(name);
        }
        return {
          value: newCategory.id.toString(),
          label: newCategory.name,
        };
      }
    } catch (err) {
      if (onError) {
        onError(err);
      }
    }
    return null;
  };

  return (
    <SearchableSelect
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      options={categoryOptions}
      loading={creatingCategory}
      creatable
      onCreateOption={handleCreateCategory}
      createLabel="Create category"
      noResultsText="No categories found"
      {...props}
    />
  );
};

export default CategorySelect;
